import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignsSearch,
  getCampaignsCreatorsMugshots,
  type ClientCampaign,
} from '@/api/beatlyApi';

/** Matches dashboard kanban channel icons */
export type DashboardChannelType = 'instagram' | 'tiktok' | 'ctv' | 'dooh' | 'youtube' | 'web';

export interface DashboardCampaignCardData {
  campaignId: string;
  title: string;
  budget: string;
  channels: DashboardChannelType[];
  creators: { avatar: string; name: string }[];
}

const SEARCH_PAGE_SIZE = 75;
const MAX_SEARCH_PAGES = 50;

function formatCampaignBudget(c: ClientCampaign): string {
  const b = c.budget;
  if (!b || typeof b.amount !== 'number') return '—';
  const cur = (b.currency ?? '').trim();
  const amt = b.amount;
  let num: string;
  if (amt >= 1_000_000) num = `${(amt / 1_000_000).toFixed(1)}M`;
  else if (amt >= 1000) num = `${Math.round(amt / 1000)}K`;
  else num = String(amt);
  return cur ? `${num} ${cur}` : num;
}

function platformToChannel(p: string): DashboardChannelType {
  const u = p.toUpperCase();
  if (u === 'INSTAGRAM') return 'instagram';
  if (u === 'TIKTOK') return 'tiktok';
  if (u === 'YOUTUBE') return 'youtube';
  if (u === 'CTV' || u.includes('CONNECTED')) return 'ctv';
  if (u === 'DOOH') return 'dooh';
  return 'web';
}

function mapPlatformsToChannels(platforms: string[]): DashboardChannelType[] {
  const seen = new Set<DashboardChannelType>();
  const out: DashboardChannelType[] = [];
  for (const p of platforms ?? []) {
    const ch = platformToChannel(p);
    if (!seen.has(ch)) {
      seen.add(ch);
      out.push(ch);
    }
  }
  return out.length ? out : ['web'];
}

function partitionCampaign(
  c: ClientCampaign,
  sixMonthsAgoMs: number
): 'planning' | 'running' | 'completed' | null {
  const st = (c.status ?? '').toUpperCase();
  const endTs = Date.parse(c.date_end);
  const endInWindow = !Number.isNaN(endTs) && endTs >= sixMonthsAgoMs;

  if (st === 'PUBLIC_DRAFT') return 'planning';
  if (st === 'DONE' || st === 'COMPLETED') {
    return endInWindow ? 'completed' : null;
  }
  if (st === 'CANCELED' || st === 'DRAFT') return null;
  return 'running';
}

function toCardData(
  c: ClientCampaign,
  creatorsByCampaign: Map<string, { avatar: string; name: string }[]>
): DashboardCampaignCardData {
  const platforms = Array.isArray(c.platforms) ? c.platforms.map(String) : [];
  return {
    campaignId: c.campaign_id,
    title: c.name ?? c.campaign_id,
    budget: formatCampaignBudget(c),
    channels: mapPlatformsToChannels(platforms),
    creators: creatorsByCampaign.get(c.campaign_id) ?? [],
  };
}

export interface DashboardRecentCampaignsResult {
  planning: DashboardCampaignCardData[];
  running: DashboardCampaignCardData[];
  completed: DashboardCampaignCardData[];
}

/**
 * Paginates GET /app/v2/client/campaigns/search, partitions into Planning / Running / Completed,
 * then loads creator mugshots in batches via GET /app/v2/client/campaigns/creators.
 */
export function useDashboardRecentCampaigns() {
  const { user } = useAuth();
  const token = getTokenFromStorage();

  const enabled = Boolean(token && user?.is_client && user?.team?.team_id);

  return useQuery({
    queryKey: ['dashboard', 'recent-campaigns', user?.team?.team_id],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<DashboardRecentCampaignsResult> => {
      const t = token ?? '';
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const sixMonthsAgoMs = sixMonthsAgo.getTime();

      const all: ClientCampaign[] = [];
      let page = 0;
      let hasMore = true;
      while (hasMore && page < MAX_SEARCH_PAGES) {
        const r = await getCampaignsSearch(
          {
            pageNumber: page,
            pageSize: SEARCH_PAGE_SIZE,
            sortBy: 'newest',
          },
          t
        );
        const batch = r.campaigns ?? [];
        all.push(...batch);
        hasMore = r.has_more === true;
        page += 1;
      }

      const planning: ClientCampaign[] = [];
      const running: ClientCampaign[] = [];
      const completed: ClientCampaign[] = [];

      for (const c of all) {
        const bucket = partitionCampaign(c, sixMonthsAgoMs);
        if (bucket === 'planning') planning.push(c);
        else if (bucket === 'running') running.push(c);
        else if (bucket === 'completed') completed.push(c);
      }

      const idSet = new Set<string>();
      for (const c of [...planning, ...running, ...completed]) {
        idSet.add(c.campaign_id);
      }
      const ids = [...idSet];

      const creatorsByCampaign = new Map<string, { avatar: string; name: string }[]>();
      if (ids.length > 0) {
        const mugResp = await getCampaignsCreatorsMugshots(ids, t);
        for (const row of mugResp.campaigns ?? []) {
          creatorsByCampaign.set(
            row.campaign_id,
            (row.creators ?? []).map((x) => ({
              avatar: (x.mugshot_url ?? '').trim(),
              name: (x.name ?? x.influencer_id ?? '').trim() || x.influencer_id,
            }))
          );
        }
      }

      return {
        planning: planning.map((c) => toCardData(c, creatorsByCampaign)),
        running: running.map((c) => toCardData(c, creatorsByCampaign)),
        completed: completed.map((c) => toCardData(c, creatorsByCampaign)),
      };
    },
  });
}
